import {
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  startTransition,
} from "react";
import type { StoredCard } from "@/types";
import type { PpsPaymentMethodDetails } from "@/modules/checkout-session";
import { usePaymentPlansActions } from "@/modules/checkout-session";
import { PAYMENT_METHOD_TYPE, type PaymentMethodOption } from "../types";
import { selectPaymentMethod } from "../services/payment-method-utils";

export function usePaymentMethods(
  savedCards: StoredCard[] | undefined,
  canPayWithApple: boolean,
  selectedPaymentMethod: PpsPaymentMethodDetails | undefined,
) {
  const { setSelectedPaymentMethod } = usePaymentPlansActions();
  const paymentMethodsRef = useRef<PaymentMethodOption[] | null>(null);
  const isSyncingRef = useRef<boolean>(false);

  const paymentMethods = useMemo(() => {
    const methods: PaymentMethodOption[] =
      savedCards?.map((card) => ({
        paymentMethodType: PAYMENT_METHOD_TYPE.STORED_CARD,
        card,
      })) ?? [];

    if (canPayWithApple) {
      return [
        { paymentMethodType: PAYMENT_METHOD_TYPE.APPLE, card: undefined },
        ...methods,
      ];
    }

    return methods;
  }, [savedCards, canPayWithApple]);

  useLayoutEffect(() => {
    paymentMethodsRef.current = paymentMethods;
  }, [paymentMethods]);

  useEffect(() => {
    if (isSyncingRef.current || paymentMethods.length === 0) {
      return;
    }

    if (!selectedPaymentMethod) {
      const applePayMethod = paymentMethods.find(
        (m) => m.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE,
      );
      const methodToSelect = applePayMethod ?? paymentMethods[0];
      if (!methodToSelect) {
        return;
      }
      startTransition(() => {
        setSelectedPaymentMethod(selectPaymentMethod(methodToSelect));
      });
    }
  }, [selectedPaymentMethod, paymentMethods, setSelectedPaymentMethod]);

  const handleSelectPaymentMethod = (method: PaymentMethodOption): void => {
    startTransition(() => {
      setSelectedPaymentMethod(selectPaymentMethod(method));
    });
  };

  const isMethodSelected = (method: PaymentMethodOption): boolean => {
    return (
      (method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE &&
        selectedPaymentMethod?.type === "apple_pay") ||
      (method.paymentMethodType !== PAYMENT_METHOD_TYPE.APPLE &&
        selectedPaymentMethod?.id === method.card?.id + "")
    );
  };

  return {
    paymentMethods,
    paymentMethodsRef,
    handleSelectPaymentMethod,
    isMethodSelected,
  };
}
